import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { DeckMeta, DeckSlide } from "./deck-data";
import "./deck.css";

/**
 * A one-layer attention-only transformer multiplied out: the direct path (a bigram table)
 * plus, per head, a QK circuit that picks the source and an OV circuit that writes the logits.
 * Pure SVG with class-based styling (the site CSP forbids inline styles).
 */
function PathExpansionFigure() {
  return (
    <svg viewBox="0 0 1000 400" role="img" aria-label="Path expansion of a one-layer attention-only transformer: tokens go through the embedding, then either straight to the unembedding as a bigram table, or through one attention head whose QK circuit chooses the source and whose OV circuit writes the logits; the two paths add">
      {/* input: tokens → W_E */}
      <rect className="fig-box fig-box-ln" x="30" y="182" width="110" height="36" rx="3" />
      <text className="fig-text" x="85" y="205">tokens · W_E</text>

      {/* direct path along the top */}
      <path className="fig-branch-write" d="M140 200 H200 V80 H690" />
      <rect className="fig-box fig-box-mlp" x="300" y="56" width="260" height="48" rx="4" />
      <text className="fig-text" x="430" y="77">direct path · Id ⊗ W_E W_U</text>
      <text className="fig-text-small" x="430" y="95">a bigram table · rank ≤ d_model</text>

      {/* per-head path along the bottom */}
      <path className="fig-branch" d="M200 200 V320 H230" />
      <rect className="fig-box fig-box-attn" x="230" y="262" width="210" height="116" rx="4" />
      <text className="fig-text" x="335" y="290">QK circuit</text>
      <text className="fig-text-small" x="335" y="312">W_E W_Q W_Kᵀ W_Eᵀ</text>
      <text className="fig-text-small" x="335" y="330">rows = dst · cols = src</text>
      <text className="fig-text-small" x="335" y="356">→ softmax → A^h</text>

      <path className="fig-branch" d="M440 320 H470" />
      <circle className="fig-plus" cx="484" cy="320" r="14" />
      <text className="fig-plus-text" x="484" y="326">×</text>
      <path className="fig-branch" d="M498 320 H530" />

      <rect className="fig-box fig-box-attn" x="530" y="262" width="210" height="116" rx="4" />
      <text className="fig-text" x="635" y="290">OV circuit</text>
      <text className="fig-text-small" x="635" y="312">W_E W_V W_O W_U</text>
      <text className="fig-text-small" x="635" y="330">rows = src · cols = out</text>
      <text className="fig-text-small" x="635" y="356">rank ≤ d_head</text>

      <text className="fig-hook" x="485" y="250">one scalar A^h[dst, src] couples them</text>

      {/* both paths add into the logits */}
      <path className="fig-branch-write" d="M740 320 H800 V200 H830" />
      <path className="fig-branch-write" d="M690 80 H800 V200" />
      <circle className="fig-plus" cx="800" cy="200" r="14" />
      <text className="fig-plus-text" x="800" y="206">+</text>
      <rect className="fig-box fig-box-ln" x="830" y="182" width="140" height="36" rx="3" />
      <text className="fig-text" x="900" y="205">logits · [pos, vocab]</text>

      {/* legend */}
      <text className="fig-legend" x="500" y="30">T = Id ⊗ W_E W_U + Σ_h A^h ⊗ (W_E W_V^h W_O^h W_U) — exact for one layer, no MLP</text>
      <text className="fig-bus-label" x="30" y="392">per head · repeated for h = 0 … 7</text>
    </svg>
  );
}

/** Renders **bold** spans inside plain content strings. */
function Rich({ text }: { text: string }) {
  const parts = text.split("**");
  return (
    <>
      {parts.map((part, index) =>
        index % 2 === 1 ? <strong key={index}>{part}</strong> : <Fragment key={index}>{part}</Fragment>,
      )}
    </>
  );
}

function readHashIndex(max: number) {
  const raw = Number(window.location.hash.replace(/^#/, ""));
  if (!Number.isInteger(raw) || raw < 1) return 0;
  return Math.min(raw, max) - 1;
}

/** Term/text rows shared by the code and figure slide kinds. */
function Points({ items }: { items?: { term: string; text: string }[] }) {
  if (!items?.length) return null;
  return (
    <dl className="slide-points">
      {items.map((item) => (
        <div key={item.term}>
          <dt>{item.term}</dt>
          <dd><Rich text={item.text} /></dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * One transformer block drawn as reads from and writes to a shared bus.
 * Pure SVG with class-based styling (the site CSP forbids inline styles).
 */
function ResidualStreamFigure() {
  return (
    <svg viewBox="0 0 1000 400" role="img" aria-label="The residual stream as a bus: attention and MLP each read the bus through LayerNorm and add their output back">
      {/* the bus */}
      <line className="fig-bus" x1="40" y1="330" x2="960" y2="330" />
      <text className="fig-bus-label" x="40" y="368">residual stream · [batch, pos, d_model]</text>

      {/* hook labels along the bus */}
      <circle className="fig-dot" cx="120" cy="330" r="6" />
      <text className="fig-hook" x="120" y="300">hook_resid_pre</text>
      <circle className="fig-dot" cx="500" cy="330" r="6" />
      <text className="fig-hook" x="500" y="300">hook_resid_mid</text>
      <circle className="fig-dot" cx="880" cy="330" r="6" />
      <text className="fig-hook" x="880" y="300">hook_resid_post</text>

      {/* attention branch */}
      <path className="fig-branch" d="M120 330 V250" />
      <rect className="fig-box fig-box-ln" x="70" y="214" width="100" height="36" rx="3" />
      <text className="fig-text" x="120" y="237">ln1</text>
      <path className="fig-branch" d="M120 214 V150 H220" />
      <rect className="fig-box fig-box-attn" x="220" y="90" width="180" height="120" rx="4" />
      <text className="fig-text" x="310" y="122">Attention</text>
      <text className="fig-text-small" x="310" y="146">reads W_Q W_K W_V</text>
      <text className="fig-text-small" x="310" y="164">hook_z → W_O</text>
      <text className="fig-text-small" x="310" y="182">+ b_O once</text>
      <path className="fig-branch-write" d="M400 150 H440 V300" />
      <text className="fig-hook" x="440" y="220">hook_attn_out</text>
      <circle className="fig-plus" cx="440" cy="330" r="14" />
      <text className="fig-plus-text" x="440" y="336">+</text>

      {/* MLP branch */}
      <path className="fig-branch" d="M500 330 V250" />
      <rect className="fig-box fig-box-ln" x="450" y="214" width="100" height="36" rx="3" />
      <text className="fig-text" x="500" y="237">ln2</text>
      <path className="fig-branch" d="M500 214 V150 H600" />
      <rect className="fig-box fig-box-mlp" x="600" y="90" width="180" height="120" rx="4" />
      <text className="fig-text" x="690" y="122">MLP</text>
      <text className="fig-text-small" x="690" y="146">W_in → gelu_new</text>
      <text className="fig-text-small" x="690" y="164">hook_pre / hook_post</text>
      <text className="fig-text-small" x="690" y="182">→ W_out</text>
      <path className="fig-branch-write" d="M780 150 H820 V300" />
      <text className="fig-hook" x="820" y="220">hook_mlp_out</text>
      <circle className="fig-plus" cx="820" cy="330" r="14" />
      <text className="fig-plus-text" x="820" y="336">+</text>

      {/* legend */}
      <text className="fig-legend" x="500" y="44">every component adds to the bus; nothing replaces it — so the final state is an exact sum of everything written</text>
    </svg>
  );
}

function SlideBody({ slide, meta }: { slide: DeckSlide; meta: DeckMeta }) {
  switch (slide.kind) {
    case "cover":
      return (
        <div className="slide-cover">
          <img className="cover-mark" src="/nc-ai-opsis-2026-08-21.png" alt="NC AI Opsis" />
          <p className="slide-eyebrow">{meta.session} · {meta.phase}</p>
          <h1>{slide.title}</h1>
          <p className="cover-sub"><Rich text={slide.subtitle} /></p>
          <ul className="cover-meta">
            {slide.meta.map((item) => <li key={item}>{item}</li>)}
          </ul>
          <p className="cover-source">{meta.source}</p>
        </div>
      );

    case "statement":
      return (
        <div className="slide-standard">
          <h2>{slide.title}</h2>
          <p className="slide-lead"><Rich text={slide.lead} /></p>
          {slide.body?.map((paragraph, index) => (
            <p className="slide-para" key={index}><Rich text={paragraph} /></p>
          ))}
          {slide.aside ? <p className="slide-aside">{slide.aside}</p> : null}
        </div>
      );

    case "bullets":
      return (
        <div className="slide-standard">
          <h2>{slide.title}</h2>
          {slide.lead ? <p className="slide-lead"><Rich text={slide.lead} /></p> : null}
          <dl className="slide-defs">
            {slide.items.map((item) => (
              <div key={item.term}>
                <dt>{item.term}</dt>
                <dd><Rich text={item.text} /></dd>
              </div>
            ))}
          </dl>
        </div>
      );

    case "numbered":
      return (
        <div className="slide-standard">
          <h2>{slide.title}</h2>
          {slide.lead ? <p className="slide-lead"><Rich text={slide.lead} /></p> : null}
          <ol className="slide-numbered">
            {slide.items.map((item, index) => (
              <li key={item.term}>
                <span className="slide-index">{String(index + 1).padStart(2, "0")}</span>
                <span>
                  <strong>{item.term}</strong>
                  <em><Rich text={item.text} /></em>
                </span>
              </li>
            ))}
          </ol>
        </div>
      );

    case "cards":
      return (
        <div className="slide-standard">
          <h2>{slide.title}</h2>
          {slide.lead ? <p className="slide-lead"><Rich text={slide.lead} /></p> : null}
          <div className={`slide-cards cards-${slide.cards.length}`}>
            {slide.cards.map((card) => (
              <article key={card.term}>
                <span className="card-label">{card.label}</span>
                <h3>{card.term}</h3>
                <p><Rich text={card.text} /></p>
              </article>
            ))}
          </div>
        </div>
      );

    case "table":
      return (
        <div className="slide-standard">
          <h2>{slide.title}</h2>
          {slide.lead ? <p className="slide-lead"><Rich text={slide.lead} /></p> : null}
          <div className="slide-table-scroll">
            <table className="slide-table">
              <thead>
                <tr>{slide.head.map((cell) => <th key={cell} scope="col">{cell}</th>)}</tr>
              </thead>
              <tbody>
                {slide.rows.map((row, rowIndex) => (
                  <tr key={`${rowIndex}-${row[0]}`}>
                    {row.map((cell, index) => (
                      index === 0
                        ? <th key={cell} scope="row"><Rich text={cell} /></th>
                        : <td key={`${row[0]}-${index}`}><Rich text={cell} /></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );

    case "links":
      return (
        <div className="slide-standard">
          <h2>{slide.title}</h2>
          {slide.lead ? <p className="slide-lead"><Rich text={slide.lead} /></p> : null}
          <div className={`slide-readings groups-${slide.groups.length}`}>
            {slide.groups.map((group) => (
              <section key={group.label}>
                <div className="reading-group-head">
                  <strong>{group.label}</strong>
                  <small>{group.note}</small>
                </div>
                <ul>
                  {group.items.map((item) => (
                    <li key={item.title}>
                      {item.href ? (
                        <a href={item.href} target="_blank" rel="noreferrer">
                          <span className="reading-title">{item.title} <b>↗</b></span>
                          <span className="reading-meta">{item.meta}</span>
                          <span className="reading-text"><Rich text={item.text} /></span>
                        </a>
                      ) : (
                        <div>
                          <span className="reading-title">{item.title}</span>
                          <span className="reading-meta">{item.meta}</span>
                          <span className="reading-text"><Rich text={item.text} /></span>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </div>
      );

    case "split":
      return (
        <div className="slide-standard">
          <h2>{slide.title}</h2>
          <div className="slide-split">
            {[slide.left, slide.right].map((side) => (
              <section key={side.label}>
                <h3>{side.label}</h3>
                {side.text.map((line, index) => (
                  <p key={index}><Rich text={line} /></p>
                ))}
              </section>
            ))}
          </div>
        </div>
      );

    case "quote":
      return (
        <div className="slide-standard">
          <h2>{slide.title}</h2>
          <div className="slide-quote-wrap">
            <blockquote className="slide-quote">
              <p>{slide.quote}</p>
              <cite>{slide.attribution}</cite>
            </blockquote>
            <div className="quote-commentary">
              {slide.body?.map((paragraph, index) => (
                <p className="slide-para" key={index}><Rich text={paragraph} /></p>
              ))}
            </div>
          </div>
        </div>
      );

    case "code":
      return (
        <div className="slide-standard">
          <h2>{slide.title}</h2>
          {slide.lead ? <p className="slide-lead"><Rich text={slide.lead} /></p> : null}
          <pre className="slide-code"><code>{slide.code}</code></pre>
          {slide.caption ? <p className="slide-code-caption"><Rich text={slide.caption} /></p> : null}
          <Points items={slide.points} />
        </div>
      );

    case "figure":
      return (
        <div className="slide-standard">
          <h2>{slide.title}</h2>
          {slide.lead ? <p className="slide-lead"><Rich text={slide.lead} /></p> : null}
          <figure className="slide-figure">
            {slide.figure === "residual-stream" ? <ResidualStreamFigure /> : null}
            {slide.figure === "path-expansion" ? <PathExpansionFigure /> : null}
          </figure>
          <Points items={slide.points} />
        </div>
      );

    case "closing":
      return (
        <div className="slide-standard">
          <h2>{slide.title}</h2>
          <p className="slide-lead"><Rich text={slide.lead} /></p>
          <dl className="slide-defs">
            {slide.items.map((item) => (
              <div key={item.term}>
                <dt>{item.term}</dt>
                <dd><Rich text={item.text} /></dd>
              </div>
            ))}
          </dl>
          <p className="slide-footer-note"><Rich text={slide.footer} /></p>
        </div>
      );
  }
}

type DeckProps = { meta: DeckMeta; slides: DeckSlide[] };

export default function Deck({ meta, slides }: DeckProps) {
  const total = slides.length;
  const [index, setIndex] = useState(() => readHashIndex(total));
  const [overview, setOverview] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);

  const go = useCallback((next: number) => {
    setIndex(() => {
      const clamped = Math.max(0, Math.min(total - 1, next));
      window.location.hash = String(clamped + 1);
      return clamped;
    });
  }, [total]);

  useEffect(() => {
    const onHashChange = () => setIndex(readHashIndex(total));
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [total]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;

      switch (event.key) {
        case "ArrowRight":
        case "PageDown":
        case " ":
          event.preventDefault();
          setOverview(false);
          go(index + 1);
          break;
        case "ArrowLeft":
        case "PageUp":
          event.preventDefault();
          setOverview(false);
          go(index - 1);
          break;
        case "Home":
          event.preventDefault();
          go(0);
          break;
        case "End":
          event.preventDefault();
          go(total - 1);
          break;
        case "n":
        case "N":
          setShowNotes((value) => !value);
          break;
        case "o":
        case "O":
          setOverview((value) => !value);
          break;
        case "Escape":
          setOverview((value) => !value);
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, index, total]);

  useEffect(() => {
    stageRef.current?.scrollTo({ top: 0 });
  }, [index]);

  const slide = slides[index];
  const chapters = useMemo(() => {
    const seen: { label: string; index: number }[] = [];
    slides.forEach((item, position) => {
      if (seen.length === 0 || seen[seen.length - 1].label !== item.chapter) {
        seen.push({ label: item.chapter, index: position });
      }
    });
    return seen;
  }, [slides]);

  return (
    <div className={`deck${showNotes ? " deck-notes-open" : ""}`}>
      <div className="deck-progress" aria-hidden="true">
        {slides.map((item, position) => (
          <span key={`tick-${position}`} className={position <= index ? "is-done" : undefined} title={item.title} />
        ))}
      </div>

      <header className="deck-bar">
        <a className="deck-back" href={meta.back.href}>{meta.back.label}</a>
        <nav className="deck-chapters" aria-label="Chapters">
          {chapters.map((chapter) => (
            <button
              key={chapter.label}
              type="button"
              className={slide.chapter === chapter.label ? "is-current" : ""}
              onClick={() => { setOverview(false); go(chapter.index); }}
            >
              {chapter.label}
            </button>
          ))}
        </nav>
        <div className="deck-tools">
          <button type="button" onClick={() => setShowNotes((value) => !value)} aria-pressed={showNotes}>
            Notes <kbd>N</kbd>
          </button>
          <button type="button" onClick={() => setOverview((value) => !value)} aria-pressed={overview}>
            Overview <kbd>O</kbd>
          </button>
        </div>
      </header>

      <main className="deck-stage" ref={stageRef}>
        <section className={`slide kind-${slide.kind}`} aria-label={`Slide ${index + 1} of ${total}`}>
          {slide.kind === "cover" ? null : (
            <p className="slide-chapter">{slide.chapter}</p>
          )}
          <SlideBody slide={slide} meta={meta} />
        </section>

        {showNotes && slide.note ? (
          <aside className="deck-notes">
            <strong>Speaker notes</strong>
            <p>{slide.note}</p>
          </aside>
        ) : null}
      </main>

      <footer className="deck-controls">
        <button type="button" onClick={() => go(index - 1)} disabled={index === 0}>← Previous</button>
        <span className="deck-counter">{String(index + 1).padStart(2, "0")} / {total}</span>
        <button type="button" onClick={() => go(index + 1)} disabled={index === total - 1}>Next →</button>
      </footer>

      {overview ? (
        <div className="deck-overview">
          <div className="overview-head">
            <div>
              <p className="slide-eyebrow">{meta.session} · {meta.window}</p>
              <h2>{meta.title}</h2>
            </div>
            <button type="button" onClick={() => setOverview(false)}>Close <kbd>Esc</kbd></button>
          </div>
          <ol className="overview-grid">
            {slides.map((item, position) => (
              <li key={item.title + position}>
                <button
                  type="button"
                  className={position === index ? "is-current" : ""}
                  onClick={() => { setOverview(false); go(position); }}
                >
                  <span className="overview-number">{String(position + 1).padStart(2, "0")}</span>
                  <span className="overview-chapter">{item.chapter}</span>
                  <span className="overview-title">{item.title}</span>
                </button>
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      <PrintDeck meta={meta} slides={slides} />
    </div>
  );
}

/** Static stack rendered only for print / PDF export, so every slide lands on its own page. */
function PrintDeck({ meta, slides }: DeckProps): ReactNode {
  return (
    <div className="deck-print" aria-hidden="true">
      {slides.map((slide, position) => (
        <section className={`slide kind-${slide.kind}`} key={`print-${position}`}>
          {slide.kind === "cover" ? null : <p className="slide-chapter">{slide.chapter}</p>}
          <SlideBody slide={slide} meta={meta} />
          <span className="print-number">{String(position + 1).padStart(2, "0")} / {slides.length}</span>
        </section>
      ))}
    </div>
  );
}
